/**
 * Hausaufgaben-Fenster und der Kreis, der es öffnet.
 */

import { h, clear } from "@/lib/dom";
import { openModal, emptyState, loading, button, textInput, toast } from "@/components/ui";
import { homeworkService, pbErrorMessage } from "@/services";
import { homeworkCard, dueLabel } from "@/pages/HomeworkPage";
import { examTermField } from "@/features/topics/examTermField";
import { navigate } from "@/app/router";
import { formatCountdown, daysUntil } from "@/lib/format";
import { addDays } from "@/features/timetable/schedule";
import type { Homework } from "@/types/models";

export interface HomeworkListSource {
  title?: string;
  subjectTitle?: string;
  accent?: string;
  load: () => Promise<Homework[]>;
  /** Wenn gesetzt: Schnellanlage im Dialog anzeigen. */
  subjectId?: string;
  topicId?: string;
}

function visibleOf(items: Homework[]): Homework[] {
  return items
    .filter((hw) => !hw.done || (daysUntil(hw.dueDate) ?? 0) >= 0)
    .sort((a, b) => a.dueDate.localeCompare(b.dueDate) || (a.duePeriod ?? 0) - (b.duePeriod ?? 0));
}

export function openHomeworkList(source: HomeworkListSource): void {
  const body = h("div", { class: "hw-modal" }, loading());

  function render(items: Homework[]): void {
    clear(body);

    // Schnellanlage nur wenn Fach + Thema bekannt (= Aufruf von der Themenseite)
    if (source.subjectId && source.topicId) {
      body.appendChild(buildCreateRow(source.subjectId, source.topicId, () => void reload()));
    }

    const visible = visibleOf(items);
    if (!visible.length) {
      body.appendChild(
        emptyState({
          icon: "checklist",
          title: "Nichts offen",
          text: "Hier steht gerade keine Hausaufgabe an.",
        })
      );
      return;
    }

    const list = h("div", { class: "hw-list" });
    let lastDate = "";
    for (const hw of visible) {
      if (hw.dueDate !== lastDate) {
        lastDate = hw.dueDate;
        list.appendChild(
          h(
            "div",
            { class: "hw-group" },
            h("span", { class: "hw-group-day" }, dueLabel({ dueDate: hw.dueDate })),
            h("span", { class: "hw-group-rel" }, formatCountdown(hw.dueDate))
          )
        );
      }
      list.appendChild(
        homeworkCard(hw, {
          subjectTitle: source.subjectTitle,
          accent: source.accent,
          onChanged: () => void reload(),
        })
      );
    }
    body.appendChild(list);
  }

  async function reload(): Promise<void> {
    render(await source.load().catch(() => [] as Homework[]));
  }

  openModal({
    title: source.title ?? "Hausaufgaben",
    body,
    guardUnsaved: false,
  });
  void reload();
}

/** Einzeilige Schnellanlage (nur Text + Frist) für den Hausaufgaben-Dialog. */
function buildCreateRow(subjectId: string, topicId: string, onCreated: () => void): HTMLElement {
  const textField = textInput({ placeholder: "Neue Aufgabe …" });
  const termField = examTermField({
    subjectId,
    from: addDays(new Date(), 1),
    defaultMode: "slot",
    autoSelect: true,
  });
  const addBtn = button({ icon: "plus", variant: "primary", size: "sm", label: "Anlegen", onClick: () => void create() });

  textField.addEventListener("keydown", (e: KeyboardEvent) => {
    if (e.key === "Enter") void create();
  });

  async function create(): Promise<void> {
    const text = textField.value.trim();
    const term = termField.getValue();
    if (!text) return toast("Bitte die Aufgabe eintragen.", "error");
    if (!term.date) return toast("Bitte eine Frist wählen.", "error");
    addBtn.disabled = true;
    try {
      await homeworkService.create({ subject: subjectId, topic: topicId, text, dueDate: term.date, duePeriod: term.periodFrom });
      textField.value = "";
      onCreated();
    } catch (err) {
      toast(pbErrorMessage(err), "error");
    } finally {
      addBtn.disabled = false;
    }
  }

  return h("div", { class: "hw-quick-create" }, textField, termField.el, addBtn);
}

type Tone = "green" | "red" | "yellow" | "gray";

function toneOf(items: Homework[]): Tone {
  if (!items.length) return "gray";
  const open = items.filter((hw) => !hw.done);
  if (!open.length) return "green";
  const soonest = Math.min(...open.map((hw) => daysUntil(hw.dueDate) ?? 99));
  if (soonest <= 0) return "red";
  if (soonest <= 3) return "yellow";
  return "gray";
}

export function homeworkBadge(opts: {
  topicId: string;
  subjectId?: string;
  subjectTitle?: string;
  accent?: string;
  items?: Homework[];
}): HTMLElement {
  // Kein eigenes Popup mehr (das hatte einen eigenen, oft veralteten
  // Datenstand) – stattdessen zur Hausaufgaben-Seite, gefiltert auf dieses
  // Fach. Zeigt immer den echten, aktuellen Stand.
  const el = h("button", {
    class: "hw-badge is-gray",
    type: "button",
    title: "Hausaufgaben",
    onclick: () => navigate(opts.subjectId ? `/homework?fach=${opts.subjectId}` : "/homework"),
  });

  function paint(items: Homework[]): void {
    const open = items.filter((hw) => !hw.done).length;
    el.className = `hw-badge is-${toneOf(items)}`;
    el.textContent = String(open);
    el.title = open
      ? `${open} offene Hausaufgabe${open === 1 ? "" : "n"}`
      : items.length
        ? "Alle Hausaufgaben erledigt"
        : "Keine Hausaufgaben";
  }

  if (opts.items) {
    paint(opts.items);
  } else {
    el.classList.add("is-loading");
    const loadFn = opts.subjectId
      ? homeworkService.listByTopicWithFallback(opts.topicId, opts.subjectId)
      : homeworkService.listByTopic(opts.topicId);
    void loadFn.catch(() => [] as Homework[]).then(paint);
  }

  return el;
}
